import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import Layout from '@/components/Layout';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { Calendar, Clock, MapPin, CheckCircle, XCircle, Home, AlertCircle, Building2 } from 'lucide-react';
import { formatDateLocal } from '@/utils/dateUtils';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AgentAssignments = () => {
  const navigate = useNavigate();
  const { token, agent } = useAuth();
  const [assignments, setAssignments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedAssignment, setSelectedAssignment] = useState(null);
  const [showActionModal, setShowActionModal] = useState(false);
  const [actionType, setActionType] = useState('');
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [note, setNote] = useState('');

  useEffect(() => {
    if (agent) {
      fetchAssignments();
    }
  }, [agent]);

  const fetchAssignments = async () => {
    try {
      const response = await axios.get(`${API}/agent/assignments`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAssignments(response.data);
    } catch (error) {
      toast.error('Failed to fetch assignments');
    } finally {
      setLoading(false);
    }
  };

  const openActionModal = (assignment, action) => {
    setSelectedAssignment(assignment);
    setActionType(action);
    setNote('');
    setShowActionModal(true);
  };

  const handleAction = async () => {
    try {
      await axios.post(
        `${API}/agent/assignments/${selectedAssignment.id}/accept`,
        {
          action: actionType,
          note: note || undefined
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Assignment ${actionType}ed successfully!`);
      setShowActionModal(false);
      fetchAssignments();
    } catch (error) {
      toast.error(`Failed to ${actionType} assignment`);
    }
  };

  const handleCreateEvent = async (assignmentId) => {
    try {
      const response = await axios.post(
        `${API}/agent/assignments/${assignmentId}/create-event`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Open house event created successfully!');
      navigate(`/events/${response.data.id}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create event');
    }
  };

  const sortByDateDesc = (arr) => [...arr].sort((a, b) => new Date(b.date) - new Date(a.date));
  const sorted = sortByDateDesc(assignments);
  const totalPages = Math.ceil(sorted.length / perPage);
  const paginated = sorted.slice((page - 1) * perPage, page * perPage);
  const showPagination = sorted.length > 10;

  return (
    <Layout>
      <div className="max-w-7xl mx-auto">
        <div className="mb-8">
          <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold text-[#1A4D2E] mb-2" style={{fontFamily: '"Playfair Display", serif'}}>
            My Assignments
          </h1>
          <p className="text-stone-600">Manage your open house assignments</p>
        </div>

        {loading ? (
          <div className="text-center py-12">
            <p className="text-stone-600">Loading assignments...</p>
          </div>
        ) : (
          <div>
            {/* Assignment List */}
            <div className="space-y-2">
              {paginated.map((assignment) => {
                const isPending = assignment.status === 'pending';
                const isAccepted = assignment.status === 'accepted';
                const isRejected = assignment.status === 'rejected';
                const borderColor = isPending ? 'border-l-orange-400' : isAccepted ? 'border-l-green-400' : 'border-l-stone-300';
                const bgColor = isPending ? 'bg-orange-50/50' : isAccepted ? 'bg-green-50/50' : 'bg-stone-50 opacity-60';
                const statusBadge = isPending
                  ? { label: 'Pending', cls: 'bg-orange-100 text-orange-700' }
                  : isAccepted
                  ? { label: 'Accepted', cls: 'bg-green-100 text-green-700' }
                  : { label: 'Declined', cls: 'bg-stone-200 text-stone-600' };

                return (
                  <Card key={assignment.id} className={`p-3 sm:p-4 border-l-4 ${borderColor} ${bgColor}`}>
                    <div className="flex items-center gap-3">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <p className="font-semibold text-sm sm:text-base text-[#1A4D2E] truncate">{assignment.property_address}</p>
                          <span className={`shrink-0 text-[10px] font-bold px-2 py-0.5 rounded-full ${statusBadge.cls}`}>{statusBadge.label}</span>
                        </div>
                        <div className="flex flex-wrap gap-x-3 gap-y-0.5 text-xs sm:text-sm text-stone-500">
                          <span><Calendar className="inline mr-1" size={12} />{formatDateLocal(assignment.date)}</span>
                          <span><Clock className="inline mr-1" size={12} />{assignment.time}{assignment.end_time ? `-${assignment.end_time}` : ''}</span>
                          {assignment.broker_name && <span><Building2 className="inline mr-1" size={12} />{assignment.broker_name}</span>}
                        </div>
                        {assignment.agent_note && isAccepted && (
                          <p className="text-xs text-green-700 mt-1 italic truncate">Note: {assignment.agent_note}</p>
                        )}
                        {assignment.agent_note && isRejected && (
                          <p className="text-xs text-red-600 mt-1 italic truncate">Reason: {assignment.agent_note}</p>
                        )}
                      </div>
                      <div className="flex gap-1.5 shrink-0">
                        {isPending && (
                          <>
                            <Button size="sm" onClick={() => openActionModal(assignment, 'accept')} className="bg-green-600 text-white text-xs rounded-full px-3 hover:bg-green-700">Accept</Button>
                            <Button size="sm" onClick={() => openActionModal(assignment, 'reject')} variant="outline" className="border border-red-300 text-red-600 text-xs rounded-full px-3 hover:bg-red-50">Decline</Button>
                          </>
                        )}
                        {isAccepted && assignment.event_id && (
                          <Button size="sm" onClick={() => navigate(`/events/${assignment.event_id}`)} variant="outline" className="border border-[#1A4D2E] text-[#1A4D2E] text-xs rounded-full px-3">View Event</Button>
                        )}
                        {isAccepted && !assignment.event_id && (
                          <Button size="sm" onClick={() => handleCreateEvent(assignment.id)} className="bg-[#1A4D2E] text-white text-xs rounded-full px-3 hover:bg-[#143D24]">Create Event</Button>
                        )}
                      </div>
                    </div>
                  </Card>
                );
              })}
            </div>

            {assignments.length === 0 && (
              <Card className="bg-white p-12 rounded-2xl card-shadow text-center">
                <Calendar className="mx-auto mb-4 text-stone-300" size={64} />
                <h3 className="text-xl font-bold text-[#1A4D2E] mb-2">No Assignments Yet</h3>
                <p className="text-stone-600">Your broker will assign you to open houses here</p>
              </Card>
            )}

            {/* Pagination */}
            {showPagination && (
              <div className="flex flex-col sm:flex-row items-center justify-between gap-3 mt-6 pt-4 border-t border-stone-200">
                <div className="flex items-center gap-2 text-sm text-stone-500">
                  <span>Show</span>
                  <select
                    value={perPage}
                    onChange={(e) => { setPerPage(Number(e.target.value)); setPage(1); }}
                    className="border border-stone-300 rounded-lg px-2 py-1 text-sm bg-white"
                  >
                    {[10, 20, 50].map(n => <option key={n} value={n}>{n}</option>)}
                  </select>
                  <span>of {sorted.length} assignments</span>
                </div>
                <div className="flex items-center gap-1">
                  <button
                    onClick={() => setPage(p => Math.max(1, p - 1))}
                    disabled={page === 1}
                    className="px-3 py-1.5 text-sm rounded-lg border border-stone-300 disabled:opacity-40 hover:bg-stone-100"
                  >
                    Prev
                  </button>
                  {Array.from({ length: totalPages }, (_, i) => i + 1).map(p => (
                    <button
                      key={p}
                      onClick={() => setPage(p)}
                      className={`w-8 h-8 text-sm rounded-lg ${p === page ? 'bg-[#1A4D2E] text-white' : 'border border-stone-300 hover:bg-stone-100 text-stone-600'}`}
                    >
                      {p}
                    </button>
                  ))}
                  <button
                    onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                    disabled={page === totalPages}
                    className="px-3 py-1.5 text-sm rounded-lg border border-stone-300 disabled:opacity-40 hover:bg-stone-100"
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Action Modal */}
        <Dialog open={showActionModal} onOpenChange={setShowActionModal}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>
                {actionType === 'accept' ? 'Accept Assignment' : 'Decline Assignment'}
              </DialogTitle>
            </DialogHeader>
            {selectedAssignment && (
              <div className="space-y-4 mt-4">
                <div className="p-4 bg-stone-50 rounded-lg">
                  <p className="text-sm font-semibold text-[#1A4D2E]">{selectedAssignment.property_address}</p>
                  <p className="text-xs text-stone-600">
                    {formatDateLocal(selectedAssignment.date)} • {selectedAssignment.time}{selectedAssignment.end_time ? ` - ${selectedAssignment.end_time}` : ''}
                  </p>
                </div>

                <div>
                  <Label htmlFor="action_note">
                    {actionType === 'accept' ? 'Add a note (optional)' : 'Reason for declining (optional)'}
                  </Label>
                  <Textarea
                    id="action_note"
                    value={note}
                    onChange={(e) => setNote(e.target.value)}
                    placeholder={actionType === 'accept' ? 'Any notes...' : 'Please provide a reason...'}
                    rows={3}
                  />
                </div>

                <div className="flex justify-end gap-2 pt-4">
                  <Button type="button" variant="outline" onClick={() => setShowActionModal(false)}>
                    Cancel
                  </Button>
                  <Button
                    onClick={handleAction}
                    className={actionType === 'accept' 
                      ? 'bg-green-600 text-white hover:bg-green-700' 
                      : 'bg-red-600 text-white hover:bg-red-700'
                    }
                  >
                    {actionType === 'accept' ? 'Accept Assignment' : 'Decline Assignment'}
                  </Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

export default AgentAssignments;
